import i18n from '../i18n';

/**
 * Timezone the backend buckets report days into (oiltrack.report.zone-id). Timestamps
 * are rendered in the same zone so the table and the daily summary agree on what a
 * day is, instead of the table silently using the browser's zone.
 */
export const REPORT_TIME_ZONE = 'Asia/Almaty';

const DEFAULT_FRACTION_DIGITS = 2;

/** Locale used for number and date formatting, following the active UI language. */
const activeLocale = (): string => {
  switch (i18n.language?.split('-')[0]) {
    case 'en':
      return 'en-GB';
    case 'kk':
      return 'kk-KZ';
    default:
      return 'ru-RU';
  }
};

export const formatNumber = (
  value: number | null | undefined,
  fractionDigits = DEFAULT_FRACTION_DIGITS,
): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return new Intl.NumberFormat(activeLocale(), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
};

/** Number followed by its unit, e.g. "1 234,50 м³". Falls back to a bare number. */
export const formatMeasurement = (
  value: number | null | undefined,
  unitType: string | null | undefined,
  unitGroup: 'volumeUnitShort' | 'massUnitShort' | 'levelUnitShort',
  fractionDigits = DEFAULT_FRACTION_DIGITS,
): string => {
  const formatted = formatNumber(value, fractionDigits);
  if (!unitType || formatted === '—') {
    return formatted;
  }
  return `${formatted} ${i18n.t(`enum.${unitGroup}.${unitType}`)}`;
};

export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(activeLocale(), {
    dateStyle: 'short',
    timeStyle: 'medium',
    timeZone: REPORT_TIME_ZONE,
  }).format(date);
};

export const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(activeLocale(), {
    dateStyle: 'medium',
    timeZone: REPORT_TIME_ZONE,
  }).format(date);
};

/**
 * How stale the newest reading is, as a short human string. This is the number that
 * tells an operator whether a station has gone quiet.
 */
export const formatAge = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';

  const minutes = Math.max(0, Math.floor((Date.now() - then) / 60000));
  if (minutes < 1) return i18n.t('report.inventory.age.justNow');
  if (minutes < 60) return i18n.t('report.inventory.age.minutes', { count: minutes });

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return i18n.t('report.inventory.age.hours', { count: hours });

  return i18n.t('report.inventory.age.days', { count: Math.floor(hours / 24) });
};

/**
 * Axis tick label, shaped to the bucket the backend chose: a minute/hour series is
 * labelled by clock time, a day/week series by date. Keeps ticks short enough not to
 * collide along the x axis.
 */
export const formatTick = (epochMs: number, bucket: string): string => {
  const opts: Intl.DateTimeFormatOptions =
    bucket === 'MINUTE' || bucket === 'HOUR'
      ? { hour: '2-digit', minute: '2-digit', hour12: false }
      : { day: '2-digit', month: 'short' };

  return new Intl.DateTimeFormat(activeLocale(), {
    ...opts,
    timeZone: REPORT_TIME_ZONE,
  }).format(new Date(epochMs));
};

/** Fuller timestamp for the chart tooltip, again shaped to the bucket. */
export const formatTooltipTime = (epochMs: number, bucket: string): string => {
  const opts: Intl.DateTimeFormatOptions =
    bucket === 'MINUTE' || bucket === 'HOUR'
      ? { dateStyle: 'short', timeStyle: 'short' }
      : { dateStyle: 'medium' };

  return new Intl.DateTimeFormat(activeLocale(), {
    ...opts,
    timeZone: REPORT_TIME_ZONE,
  }).format(new Date(epochMs));
};

export const formatPercent = (part: number, total: number): string => {
  if (!total) return '—';
  return `${Math.round((part / total) * 100)}%`;
};

/**
 * `datetime-local` inputs speak wall-clock time with no zone. Values are interpreted
 * in REPORT_TIME_ZONE so a range typed by the user means the same thing the backend
 * aggregates by.
 */
export const localInputToIso = (value: string): string | undefined => {
  if (!value?.trim()) return undefined;
  const asUtc = new Date(`${value}:00Z`);
  if (Number.isNaN(asUtc.getTime())) return undefined;

  // Offset of the report zone at that moment, derived by formatting the instant in
  // both zones and diffing - avoids hardcoding a fixed +05:00 that DST could break.
  const offsetMs = zoneOffsetMs(asUtc);
  return new Date(asUtc.getTime() - offsetMs).toISOString();
};

export const isoToLocalInput = (iso: string | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const shifted = new Date(date.getTime() + zoneOffsetMs(date));
  return shifted.toISOString().slice(0, 16);
};

const zoneOffsetMs = (at: Date): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: REPORT_TIME_ZONE,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);

  const lookup = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const asUtc = Date.UTC(
    lookup('year'),
    lookup('month') - 1,
    lookup('day'),
    lookup('hour') % 24,
    lookup('minute'),
    lookup('second'),
  );
  return asUtc - at.getTime();
};

export type DateRangePreset = 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth';

/** Preset ranges, expressed as ISO instants covering whole days in the report zone. */
export const presetRange = (preset: DateRangePreset): { from: string; to: string } => {
  const now = new Date();
  const startOfToday = startOfDayInZone(now);
  const day = 24 * 60 * 60 * 1000;

  switch (preset) {
    case 'today':
      return { from: startOfToday.toISOString(), to: now.toISOString() };
    case 'yesterday':
      return {
        from: new Date(startOfToday.getTime() - day).toISOString(),
        to: new Date(startOfToday.getTime() - 1).toISOString(),
      };
    case 'last7':
      return { from: new Date(startOfToday.getTime() - 6 * day).toISOString(), to: now.toISOString() };
    case 'last30':
      return { from: new Date(startOfToday.getTime() - 29 * day).toISOString(), to: now.toISOString() };
    case 'thisMonth': {
      const parts = zoneParts(now);
      const from = new Date(`${parts.year}-${parts.month}-01T00:00:00Z`);
      return {
        from: new Date(from.getTime() - zoneOffsetMs(from)).toISOString(),
        to: now.toISOString(),
      };
    }
  }
};

const startOfDayInZone = (at: Date): Date => {
  const parts = zoneParts(at);
  const midnight = new Date(`${parts.year}-${parts.month}-${parts.day}T00:00:00Z`);
  return new Date(midnight.getTime() - zoneOffsetMs(midnight));
};

const zoneParts = (at: Date) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: REPORT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at);
  const value = (type: string) => parts.find((p) => p.type === type)?.value ?? '01';
  return { year: value('year'), month: value('month'), day: value('day') };
};
